import { SubmitHandler } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { Separator } from '@givebutter/primitives/separator';
import { useToast } from '@givebutter/primitives/toast';
import { createCustomField } from 'api/campaigns';
import * as CampaignToolsCustomFieldForm from 'assets/js/pages/campaign-settings/campaign-tools/CampaignToolsCustomFieldForm';
import * as Dialog from 'components/dialog';
import * as Form from 'components/form';
import { useForceRender } from 'customHooks/useForceRender';
import { omit } from 'lodash';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { CampaignCustomField } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface AddCustomFieldProps extends Dialog.DialogProps {
    type: string | null;
    campaignId: number;
}

const CampaignToolsAddCustomField = ({
    children,
    type,
    campaignId,
    ...rest
}: AddCustomFieldProps) => {
    const { key, forceRender } = useForceRender();
    const queryClient = useQueryClient();
    const { success, error } = useToast();

    const methods = Form.useGivebutterForm<CampaignCustomField>({
        defaultValues: {
            type,
            title: '',
            description: '',
            required: false,
            answers: [{ value: '' }],
        },
    });

    const { reset, handleSubmit, watch } = methods;

    const formType = watch('type');
    const isOptionType = formType && ['select', 'multiselect', 'radio'].includes(formType);

    const { mutate: submit, isPending: isLoading } = useMutation({
        mutationFn: (data: CampaignCustomField) => {
            const sanitizedAnswers = isOptionType
                ? data?.answers?.map(answer => (typeof answer === 'object' ? answer.value : answer))
                : [];

            let submissionData = {
                ...(isOptionType ? data : omit(data, 'answers')),
                ...(isOptionType && { answers: sanitizedAnswers }),
            };

            return createCustomField(campaignId, submissionData);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: campaignQueryKeys.customFields(campaignId),
            });
            success(`The ${type} custom field has been created!`);
            reset();
            forceRender();
        },
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data.message ?? 'Something went wrong. Please try again.');
        },
    });

    return (
        <Dialog.Root key={key} {...rest}>
            {children}
            <Dialog.Portal>
                <Dialog.Overlay />
                <Dialog.Content>
                    <div className="w-full border-0">
                        <Form.GivebutterFormProvider {...methods}>
                            <form
                                onSubmit={handleSubmit(
                                    submit as SubmitHandler<CampaignCustomField>,
                                )}
                            >
                                <CampaignToolsCustomFieldForm.Container>
                                    <CampaignToolsCustomFieldForm.Title />
                                    <CampaignToolsCustomFieldForm.Description />
                                    <CampaignToolsCustomFieldForm.IsRequired />

                                    <Separator />

                                    {isOptionType && <CampaignToolsCustomFieldForm.Answers />}

                                    <CampaignToolsCustomFieldForm.Submit isLoading={isLoading}>
                                        <Dialog.Trigger asChild>
                                            <Button variant="transparent">Cancel</Button>
                                        </Dialog.Trigger>
                                    </CampaignToolsCustomFieldForm.Submit>
                                </CampaignToolsCustomFieldForm.Container>
                            </form>
                        </Form.GivebutterFormProvider>
                    </div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

const CampaignToolsAddCustomFieldTrigger = Dialog.Trigger;
const Root = CampaignToolsAddCustomField;

const Trigger = CampaignToolsAddCustomFieldTrigger;

export { CampaignToolsAddCustomField, CampaignToolsAddCustomFieldTrigger, Root, Trigger };
